import { objectKeysToCamelCase, objectKeysToSnakeCase } from '@mint-lib/api';
import { createMintSocketBuilder } from '@mint-lib/api-zod-support/socket';
import type {
  SocketBuilderConfig,
  SocketClient,
} from '@mint-lib/api-zod-support/socket';
import { z } from 'zod';

import {
  AgentAudioChunkSchema,
  AuthenticatePayloadSchema,
  BINARY_TOPICS,
  CHAT_SOCKET_TOPICS,
  ChatSocketErrorEventSchema,
  IncomingChatMessageSchema,
  OutgoingChatMessageSchema,
  VoiceChunkSchema,
} from './schemas.js';

// Exported so the topic gate is testable
export const formatChatSocketMessage = (topic: string, payload: unknown) =>
  BINARY_TOPICS.has(topic) || !payload || typeof payload !== 'object'
    ? payload
    : objectKeysToSnakeCase(payload as Record<string, unknown>);

export const parseChatSocketMessage = ({
  event,
  payload,
}: {
  event: string;
  payload: unknown;
}) => ({
  topic: event,
  payload:
    BINARY_TOPICS.has(event) || !payload || typeof payload !== 'object'
      ? payload
      : objectKeysToCamelCase(payload as Record<string, unknown>),
});

export type ChatSocketClient = SocketClient & {
  connect(): void;
  disconnect(): void;
  connected: boolean;
};

export type CreateChatSocketOptions = {
  client: ChatSocketClient;
};

const CONNECT_EVENT = 'connect';

export const createChatSocket = ({ client }: CreateChatSocketOptions) => {
  const config: SocketBuilderConfig = {
    onValidationError: (error, topic, raw) =>
      report('validation failed', topic, error, raw),
    formatMessage: formatChatSocketMessage,
    parseMessage: parseChatSocketMessage,
  };

  const socket = createMintSocketBuilder(config);
  socket.provideClient(client);

  const reported = new Set<string>();

  const reportOnce = (topic: string, error: unknown, payload: unknown) => {
    if (reported.has(topic)) {
      return;
    }

    reported.add(topic);
    report('outgoing payload rejected', topic, error, payload);
  };

  /**
   * Sends never throw: `createSendHandler` parses with no `try`/`catch`, and
   * `sendVoiceChunk` runs in the recorder loop outside React. The boolean says
   * whether the payload left, so a caller can keep it queued.
   *
   * The absent `ackSchema` is the whole ack guard: an ack send returns a
   * Promise that this wrapper would swallow.
   */
  const defineGuardedSend = <Schema extends z.ZodType>(options: {
    topic: string;
    payloadSchema: Schema;
  }) => {
    const send = socket.defineSend(options);

    const guarded = (payload: z.input<Schema>) => {
      try {
        send(payload);

        return true;
      } catch (error) {
        reportOnce(options.topic, error, payload);

        return false;
      }
    };

    return Object.assign(guarded, { config: options });
  };

  return {
    connect: () => {
      client.connect();
    },
    disconnect: () => {
      client.disconnect();
    },
    /** Live connectivity, read at call time. */
    getConnected: () => client.connected,
    onConnect: (handler: () => void) => {
      client.on(CONNECT_EVENT, handler);

      return () => {
        client.off(CONNECT_EVENT, handler);
      };
    },

    authenticate: defineGuardedSend({
      topic: CHAT_SOCKET_TOPICS.authenticate,
      payloadSchema: AuthenticatePayloadSchema,
    }),
    sendChatMessage: defineGuardedSend({
      topic: CHAT_SOCKET_TOPICS.message,
      payloadSchema: OutgoingChatMessageSchema,
    }),
    startVoiceInput: defineGuardedSend({
      topic: CHAT_SOCKET_TOPICS.voiceInputStart,
      payloadSchema: z.literal('ok'),
    }),

    /** No schema, no ack: the raw emit is the whole contract. */
    cancelVoiceInput: () => {
      client.emit(CHAT_SOCKET_TOPICS.voiceInputCancel);
    },
    sendVoiceChunk: defineGuardedSend({
      topic: CHAT_SOCKET_TOPICS.voiceInputChunk,
      payloadSchema: VoiceChunkSchema,
    }),
    muteVoiceInput: defineGuardedSend({
      topic: CHAT_SOCKET_TOPICS.voiceInputMute,
      payloadSchema: z.literal('ok'),
    }),

    onAuthenticated: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.authenticate,
      payloadSchema: z.unknown(),
    }),
    onChatMessage: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.message,
      payloadSchema: IncomingChatMessageSchema,
    }),
    /** Schema-free twin, so the chat-list invalidation survives a rejected frame. */
    onChatMessageFrame: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.message,
      payloadSchema: z.unknown(),
    }),
    onChatError: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.error,
      payloadSchema: ChatSocketErrorEventSchema,
    }),
    onVoiceSessionUpdated: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.sessionUpdated,
      payloadSchema: z.unknown(),
    }),
    onVoiceResponseCreated: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.responseCreated,
      payloadSchema: z.unknown(),
    }),
    onUserSpeechStarted: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.userSpeechStarted,
      payloadSchema: z.unknown(),
    }),
    onUserSpeechStopped: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.userSpeechStopped,
      payloadSchema: z.unknown(),
    }),
    onAgentAudioChunk: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.agentAudioChunk,
      payloadSchema: AgentAudioChunkSchema,
    }),
    onAgentResponseDone: socket.defineSubscribe({
      topic: CHAT_SOCKET_TOPICS.agentResponseDone,
      payloadSchema: z.unknown(),
    }),
  };
};

const report = (
  message: string,
  topic: string,
  error: unknown,
  raw: unknown,
) => {
  if (import.meta.env.DEV) {
    console.error(`[AiAssistantSocket] ${message}`, topic, error, raw);
  }
};

export type ChatSocket = ReturnType<typeof createChatSocket>;
